import React, {
  forwardRef,
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  useId,
} from 'react';
import { motion } from 'framer-motion';
import { Box, HiddenInput, LabelText, Wrapper } from "./checkbox.styles";

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  checked?: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  indeterminate?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      onChange,
      indeterminate = false,
      label,
      disabled,
      id,
      ...rest
    },
    ref: Ref<HTMLInputElement>
  ) => {
    const autoId = useId();
    const inputId = id || autoId;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
      onChange(e.target.checked);

    let icon: ReactNode = null;
    if (indeterminate && !checked) {
      icon = <rect x="2" y="7" width="12" height="2" rx="1" />;
    } else if (checked) {
      icon = <path d="M3 9l3 3 7-7" fill="none" strokeWidth="2" />;
    }

    return (
      <Wrapper disabled={disabled} htmlFor={inputId}>
        <HiddenInput
          {...rest}
          id={inputId}
          ref={ref}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
        />
        <Box disabled={disabled}>
          <motion.svg
            key={checked ? 'checked' : indeterminate ? 'indeterminate' : 'none'}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            initial={{ scale: 0.2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.2, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            {icon}
          </motion.svg>
        </Box>
        {label && <LabelText disabled={disabled}>{label}</LabelText>}
      </Wrapper>
    );
  }
);

Checkbox.displayName = 'Checkbox';
export default Checkbox;
